// @flow
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Slide from '@mui/material/Slide';

import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import { GlobalEvent } from '../GlobalContext';
import DocxViewer from './DocxViewer';
import ExcelViewer from './ExcelViewer';
import TextViewer from './TextViewer';
import { isWordFile, isExcelFile, isTextFile, getFileExt, openOfficeFileInNewTab } from './fileViewerUtils';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function FileViewerDialog(props) {
  const {
    open: propOpen,
    onClose: propOnClose,
    fileUrl: propFileUrl,
    filename: propFilename,
    title: propTitle,
    canDownload: propCanDownload,
  } = props;

  const [gEvent, gEventDispatch] = useContext(GlobalEvent);

  const [isOpen, setIsOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState({
    url: '',
    filename: '',
    title: '',
    canDownload: true,
  });
  const [isFullScreen, setIsFullScreen] = useState(true);

  // Sync with propOpen if provided as controlled component
  useEffect(() => {
    if (propOpen !== undefined) {
      setIsOpen(propOpen);
      if (propOpen) {
        setCurrentFile({
          url: propFileUrl || '',
          filename: propFilename || '',
          title: propTitle || propFilename || '',
          canDownload: propCanDownload !== undefined ? propCanDownload : true,
        });
      }
    }
  }, [propOpen, propFileUrl, propFilename, propTitle, propCanDownload]);

  // Listen to GlobalEvent openFileViewer - open in new browser tab
  useEffect(() => {
    if (gEvent?.openFileViewer) {
      const viewerData = gEvent.openFileViewer;
      openOfficeFileInNewTab({
        url: viewerData.url || '',
        filename: viewerData.filename || '',
        title: viewerData.title || viewerData.filename || 'Document',
        canDownload: viewerData.canDownload !== undefined ? viewerData.canDownload : true,
      });
      gEventDispatch({ openFileViewer: null });
    }
  }, [gEvent?.openFileViewer]);

  const handleClose = () => {
    setIsOpen(false);
    if (propOnClose) {
      propOnClose();
    }
    // Clear global event payload
    if (gEvent?.openFileViewer) {
      gEventDispatch({ openFileViewer: null });
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  const handleOpenInNewWindow = () => {
    if (currentFile.url) {
      openOfficeFileInNewTab({
        url: currentFile.url,
        filename: currentFile.filename,
        title: currentFile.title,
        canDownload: currentFile.canDownload,
      });
      handleClose();
    }
  };

  const ext = getFileExt(currentFile.filename);
  const isWord = isWordFile(currentFile.filename);
  const isExcel = isExcelFile(currentFile.filename);
  const isText = isTextFile(currentFile.filename);

  const themeColor = isExcel ? '#107c41' : (isText ? '#0f766e' : '#185abd');

  return (
    <Dialog
      fullScreen={isFullScreen}
      maxWidth="xl"
      fullWidth
      open={isOpen}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          bgcolor: '#1e293b',
          display: 'flex',
          flexDirection: 'column',
          height: isFullScreen ? '100vh' : '90vh',
          borderRadius: isFullScreen ? 0 : 2,
        },
      }}
    >
      {/* Top Header Bar */}
      <DialogTitle
        sx={{
          m: 0,
          px: 2.5,
          py: 1.25,
          bgcolor: '#0f172a',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #334155',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, mr: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: themeColor,
              color: '#ffffff',
              flexShrink: 0,
            }}
          >
            {isExcel ? <TableChartIcon fontSize="small" /> : (isText ? <ArticleIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />)}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  maxWidth: { xs: 200, sm: 400, md: 600 },
                }}
              >
                {currentFile.title || currentFile.filename || 'Document Preview'}
              </Typography>

              {ext && (
                <Chip
                  label={ext.replace('.', '').toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: themeColor,
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}
            </Box>

            {currentFile.filename && currentFile.title !== currentFile.filename && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: '#94a3b8',
                  display: 'block',
                  fontSize: '0.75rem',
                  maxWidth: { xs: 200, sm: 400, md: 600 },
                }}
              >
                {currentFile.filename}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Dialog Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Open in new window">
            <IconButton size="small" onClick={handleOpenInNewWindow} sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={handleToggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
              {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Close Viewer">
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: '#94a3b8',
                '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' },
                ml: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Main Content Area */}
      <DialogContent
        sx={{
          p: 0,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: '#1e293b',
        }}
      >
        {isWord && (
          <DocxViewer
            fileUrl={currentFile.url}
            filename={currentFile.filename}
            canDownload={currentFile.canDownload}
          />
        )}

        {isExcel && (
          <ExcelViewer
            fileUrl={currentFile.url}
            filename={currentFile.filename}
            canDownload={currentFile.canDownload}
          />
        )}

        {isText && (
          <TextViewer
            fileUrl={currentFile.url}
            filename={currentFile.filename}
            canDownload={currentFile.canDownload}
          />
        )}

        {!isWord && !isExcel && !isText && (
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
              Unsupported document preview format
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
              In-browser preview is available for Word (.docx), Excel (.xlsx, .xls, .csv), and Text (.txt) documents.
            </Typography>
            {currentFile.canDownload && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => window.open(currentFile.url, '_blank')}
                sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
              >
                Download File
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

FileViewerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  fileUrl: PropTypes.string,
  filename: PropTypes.string,
  title: PropTypes.string,
  canDownload: PropTypes.bool,
};

FileViewerDialog.defaultProps = {
  open: undefined,
  onClose: null,
  fileUrl: '',
  filename: '',
  title: '',
  canDownload: true,
};
